import React, { useCallback, useState, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { components, type OptionType, type MenuListComponentProps } from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type { MenuListPropsType } from '../../../common/types.tsx';
import ErrorMessage from './error-message/index.tsx';
import LoadingMessage from './loading-message/index.tsx';
import ShowMoreButton from './show-more-button/index.tsx';

type CustomProps = {
	selectProps: {
		menuListProps: MenuListPropsType;
	};
};

export const MenuList = (props: MenuListComponentProps<OptionType> & CustomProps) => {
	const [clickCount, setClickCount] = useState(1);

	const {
		selectProps: {
			menuListProps: {
				error: { isError, onRetry },
				handleShowMore,
				isLoading,
				isPaginationLoading,
				showShowMoreButton,
			},
		},
	} = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnShowMore = useCallback(
		(mouseEvent?: MouseEvent<HTMLElement>, analyticsEvent?: UIAnalyticsEvent) => {
			setClickCount(clickCount + 1);

			if (fg('jql_basic_picker_infinite_scroll')) {
				fireUIAnalytics(
					createAnalyticsEvent({
						actionSubject: 'options',
						action: 'scrolled',
					}),
					'filterSearchShowMore',
				);
			} else {
				analyticsEvent &&
					fireUIAnalytics(analyticsEvent, 'filterSearchShowMore', {
						clickCount,
						// If number of clicks is 0, button was actioned using the keyboard
						keyboard: mouseEvent?.detail === 0,
					});
			}

			handleShowMore();
		},
		[clickCount, handleShowMore, createAnalyticsEvent],
	);

	if (isLoading && clickCount > 1) {
		setClickCount(1);
	}

	if (fg('jql_basic_picker_infinite_scroll')) {
		return (
			<>
				<components.MenuList {...props}>
					{isLoading && <LoadingMessage />}
					{!isLoading && isError && <ErrorMessage onRetry={onRetry} />}
					{!isLoading && !isError && (
						<>
							{props.children}
							{showShowMoreButton && (
								<ShowMoreButton
									onShowMore={handleOnShowMore}
									isPaginationLoading={isPaginationLoading}
								/>
							)}
						</>
					)}
				</components.MenuList>
			</>
		);
	}

	return (
		<components.MenuList {...props}>
			{isLoading && <LoadingMessage />}
			{!isLoading && isError && <ErrorMessage onRetry={onRetry} />}
			{!isLoading && !isError && (
				<>
					{props.children}
					{isPaginationLoading && (
						<LoadingWrapper testid="jql-builder-basic-picker.ui.async-select.menu-list.loading">
							<Spinner />
						</LoadingWrapper>
					)}
					{showShowMoreButton && !isPaginationLoading && (
						<ShowMoreButton onShowMore={handleOnShowMore} />
					)}
				</>
			)}
		</components.MenuList>
	);
};

export default MenuList;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingWrapper = styled.div<{ testid: string }>({
	display: 'flex',
	justifyContent: 'center',
	marginTop: token('space.100'),
	marginRight: token('space.0'),
	marginBottom: token('space.100'),
	marginLeft: token('space.0'),
});
